import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import en from './en.json';
import fr from './fr.json';
import es from './es.json';

const translations = { en, fr, es };
const SUPPORTED_LANGUAGES = ['en', 'fr', 'es'];
const DEFAULT_LANGUAGE = 'en';
const STORAGE_KEY = 'tapelligence_lang';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const getLanguageFromPath = () => {
    const pathLang = location.pathname.split('/')[1];
    if (SUPPORTED_LANGUAGES.includes(pathLang)) return pathLang;
    // Admin/showroom pages — use localStorage preference
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (SUPPORTED_LANGUAGES.includes(stored)) return stored;
    } catch {}
    return DEFAULT_LANGUAGE;
  };

  const [language, setLanguage] = useState(getLanguageFromPath);

  useEffect(() => {
    const pathLang = getLanguageFromPath();
    if (pathLang !== language) {
      setLanguage(pathLang);
    }
  }, [location.pathname]);

  const changeLanguage = (newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) return;
    
    const currentPath = location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean);
    
    if (SUPPORTED_LANGUAGES.includes(pathParts[0])) {
      // Public route: navigate with language prefix
      pathParts[0] = newLang;
      const newPath = '/' + pathParts.join('/');
      setLanguage(newLang);
      navigate(newPath);
    } else {
      // Admin/showroom route: save to localStorage, update state only
      try { localStorage.setItem(STORAGE_KEY, newLang); } catch {}
      setLanguage(newLang);
    }
  };

  const t = (key) => {
    const keys = key.split('.');
    
    // Try current language first
    let value = translations[language];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fall back to English
        let fallback = translations['en'];
        for (const fk of keys) {
          if (fallback && typeof fallback === 'object' && fk in fallback) {
            fallback = fallback[fk];
          } else {
            return key;
          }
        }
        return fallback;
      }
    }
    
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
